// The classic look's claim tile (Nic 2026-10-24): main's SalesCard as it was
// before the redesign, so people can stay on the old dashboard until they
// pick a look. Two changes only: the menu holds just Create Meeting Invite
// (the other actions stay removed), and Waiting on documents shows.
import React from 'react';
import { Hourglass } from 'lucide-react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import numeral from 'numeral';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileContract,
  faUserTie,
  faUser,
  faGripVertical,
  faTasks,
  faUserMd,
  faBell,
  faMapMarkerAlt,
} from '@fortawesome/free-solid-svg-icons';

import { ucwords } from '../../../../lib/strings';
import CustomDropdown from '../../../common/CustomDropdown';
import { NO_MEETING_STATUSES } from '../components/SalesCard';

const StyledItem = styled.div`
  width: 100%;
  padding: 0.5rem;
  background: #fff;
  margin-bottom: 0.5rem;
  border: 1px solid #dfdfdf;
  border-radius: 5px;
  box-shadow: 0px 10px 4px -8px rgba(0, 0, 0, 0.1);
  color: rgba(33, 37, 41, 0.75);

  & a {
    text-decoration: none;
  }

  & strong {
    color: #000;
  }
`;

const StyledDragHandle = styled.div`
  color: #999;
  text-center;
`;

const AddressMissingDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffc107;
  color: #212529;
  font-size: 11px;
  cursor: help;
`;

const ClassicSalesCard = ({ card, handleProps, className, actions, isAdmin }) => {
  const daysSince = moment().diff(moment(card.claimUpdatedOn), 'days');
  const financialDisplay = card._actualValue
    ? numeral(card._actualValue).format('$0,0')
    : `${numeral(card._minValue).format('$0,0')} - ${numeral(
        card._maxValue,
      ).format('$0,0')}`;

  const statusKey = String(card.claimStatus || '').toLowerCase();
  const entryLane =
    statusKey === 'lead' ||
    statusKey === 'leads' ||
    statusKey === 'intake' ||
    statusKey === 'awaiting medical records';
  // "Claim Manager" only earns its label when a real CM (claims_manager /
  // claims_admin) holds the claim. Entry lanes always show Onboarding as
  // before; later lanes fall back to the Onboarding line ONLY when the
  // assignee isn't actually a CM (an onboarding stand-in, a director) — a
  // tile calling Jorge "Claim Manager" told the same lie the profile card
  // did (Nic, 2026-08-03). That fallback renders in red: it doubles as the
  // "this claim still needs a Claims Manager" flag.
  const assignedIsRealCm = ['claims_manager', 'claims_admin'].includes(
    card.assignedToUserType,
  );
  const showOnboarding = entryLane || !assignedIsRealCm;
  const needsCmFlag = !entryLane && !assignedIsRealCm;

  // Surface the address indicator only when the client is on the gated
  // population (requireAddress = true) AND their address is not verified.
  // Restrict to entry-stage lanes where the gate fires; later lanes don't
  // need the cue.
  const showAddressMissingBadge =
    card._clientRequireAddress === true &&
    card._clientAddressVerified === false &&
    (statusKey === 'intake' || statusKey === 'awaiting medical records');

  const roleLabel = showOnboarding ? 'Onboarding' : 'Claim Manager';
  const roleIcon = showOnboarding ? faUser : faUserTie;

  const roleAssignedId = showOnboarding
    ? card.onboardingAssignedUserId || card.onboardingAssignedToUserId
    : card.assignedToUserId || card.claimAssignedToUserId;

  const roleAssignedName = showOnboarding
    ? `${card.onboardingAssignedUserFirstName || ''} ${
        card.onboardingAssignedUserLastName || ''
      }`.trim()
    : `${card.assignedToUserFirstName || ''} ${card.assignedToUserLastName || ''}`.trim();

  const roleDisplayName = roleAssignedId
    ? roleAssignedName || 'Assigned'
    : 'Unassigned';

  return (
    <StyledItem className={className || ''}>
      <div className="d-flex justify-content-between align-items-start">
        <Link
          to={`/admin/profile/${card.claimId}`}
          className="m-0 p-0 mb-1 fs-5"
        >
          <strong>
            {card.userFirstName} {card.userLastName}
          </strong>
        </Link>
        <StyledDragHandle {...handleProps}>
          <FontAwesomeIcon icon={faGripVertical} />
        </StyledDragHandle>
      </div>
      <div
        className="d-flex align-items-start mb-1 flex-wrap"
        style={{ columnGap: 12 }}
      >
        <OverlayTrigger
          overlay={
            <Tooltip id={`${card.claimId}-value`}>
              {needsCmFlag
                ? roleAssignedId
                  ? `No Claims Manager assigned yet. Onboarding: ${roleDisplayName}.`
                  : 'No Claims Manager assigned yet, and onboarding is unassigned too.'
                : roleAssignedId
                  ? `${roleLabel} assigned to ${roleDisplayName}`
                  : `${roleLabel} is unassigned.`}
            </Tooltip>
          }
        >
          <div
            className="mb-2"
            style={{
              flex: '1 1 170px',
              minWidth: 140,
              paddingRight: 12,
            }}
          >
            <small
              className={`text-uppercase ${needsCmFlag ? 'text-danger' : 'text-muted'}`}
              style={{
                display: 'block',
                fontSize: 10,
                letterSpacing: 0.6,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.1,
              }}
            >
              {roleLabel}
            </small>
            <span
              className={
                needsCmFlag || !roleAssignedId ? 'text-danger' : 'text-muted'
              }
              style={{
                display: 'block',
                fontSize: 13,
                minWidth: 0,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                lineHeight: 1.15,
              }}
            >
              <FontAwesomeIcon icon={roleIcon} /> {roleDisplayName}
            </span>
          </div>
        </OverlayTrigger>
        {isAdmin ? (
          <p className="mb-2 ms-auto" style={{ textAlign: 'right' }}>
            <OverlayTrigger
              overlay={
                <Tooltip
                  id={`${card.claimId}-value`}
                >{`Contract potential value for ${card.claimCurrentRating}% - 100%`}</Tooltip>
              }
            >
              <span>
                <FontAwesomeIcon icon={faFileContract} /> {financialDisplay}
              </span>
            </OverlayTrigger>
          </p>
        ) : null}
      </div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="mb-0">{ucwords(card.claimStatus)}</p>
        <p className="mb-0">
          <OverlayTrigger
            overlay={
              <Tooltip
                id={`${card.claimId}-update`}
              >{`Last updated ${daysSince} days ago on ${moment(
                card.claimUpdatedOn,
              ).format('MM/DD/YYYY')}`}</Tooltip>
            }
          >
            <span className={daysSince >= 14 ? 'text-danger' : ''}>
              {moment(card.claimUpdatedOn).format('MM/DD/YYYY')}
            </span>
          </OverlayTrigger>
        </p>
      </div>
      <div className="d-flex justify-content-between align-items-center mb-1">
        <div className="d-flex justify-content-end align-items-center gap-3">
          <OverlayTrigger
            overlay={
              <Tooltip
                id={`${card.claimId}-tasks`}
              >{`Completed ${card._completedTasks} of ${card._totalTasks} tasks. Current: ${card._nextTask}`}</Tooltip>
            }
          >
            <small>
              <FontAwesomeIcon icon={faTasks} /> {card._completedTasks}/
              {card._totalTasks}
            </small>
          </OverlayTrigger>
          <small className="d-none">
            <FontAwesomeIcon icon={faUserMd} />
          </small>
          <OverlayTrigger
            overlay={
              <Tooltip
                id={`${card.claimId}-alert`}
              >{`Alerts coming soon.`}</Tooltip>
            }
          >
            <small className="">
              <FontAwesomeIcon icon={faBell} />
            </small>
          </OverlayTrigger>
        </div>
        <div className="d-flex align-items-center" style={{ columnGap: 6 }}>
          {card.claimWaitingOnDocsAt ? (
            <OverlayTrigger
              overlay={
                <Tooltip id={`${card.claimId}-waiting`}>
                  {`Waiting on documents since ${moment(card.claimWaitingOnDocsAt).format('MM/DD/YYYY')}${
                    card.claimWaitingOnDocsNote ? `: ${card.claimWaitingOnDocsNote}` : ''
                  }`}
                </Tooltip>
              }
            >
              <span aria-label="Waiting on documents" className="d-inline-flex align-items-center" style={{ color: '#d97706' }}>
                <Hourglass size={15} strokeWidth={2.25} />
              </span>
            </OverlayTrigger>
          ) : null}
          {showAddressMissingBadge ? (
            <OverlayTrigger
              overlay={
                <Tooltip id={`${card.claimId}-address-missing`}>
                  Please add a verified address for this client before
                  proceeding.
                </Tooltip>
              }
            >
              <AddressMissingDot aria-label="Address missing">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </AddressMissingDot>
            </OverlayTrigger>
          ) : null}
          {/* Same rule as the new tile's meeting button: never on Invoiced,
              Payment Plan, Paid, Dispute Hold or Dead. */}
          {actions?.length && !NO_MEETING_STATUSES.includes(statusKey) ? (
            <CustomDropdown menuItems={actions} clickData={card} />
          ) : null}
        </div>
      </div>
    </StyledItem>
  );
};

export default ClassicSalesCard;
